// @ts-strict-ignore
import type {Moment} from 'moment';
import moment from 'moment';
// isolating moment-timezone to avoid type conflicts with other imports of moment.
import momentTimezone from 'moment-timezone';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import trim from 'lodash/trim';

import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {abbreviateNumber, commaizeNumber, trimZeroesNumber} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {NAME_PROPERTIES, ProfileNameProperties} from 'common/util/profiles';
import {COUNTRY_CODES} from 'common/util/country-codes';
import {isURL} from 'common/util/url';
import {formatDate} from 'common/util/date';
import {reporter} from 'common/report/error-reporting';
import {ConstantPropertyValues, ConstantPropertyValuesInfo, propertiesUtils} from 'common/report/util/properties';
import {MPProperties} from 'common/constants';
import {ResourceType} from 'common/report/constants';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';

export const SHORT_DISPLAY_DATE_FORMAT = `MMM DD, YYYY`;
export const FULL_DISPLAY_DATE_FORMAT = `ddd, MMM D, YYYY h:mm A`;
export const SECOND_DISPLAY_DATE_FORMAT = `h:mm:ss a, ddd, MMM D, YYYY`;
export const MILLISECOND_DISPLAY_DATE_FORMAT = `h:mm:ss.SSS a, ddd, MMM D, YYYY`;

export enum PropertyRenderType {
  Email = `email`,
  Link = `link`,
  String = `string`,
}

export type ValidPropertyValue = string | number | boolean | any[];

interface BaseRenderProperty {
  raw: ValidPropertyValue;
  type: PropertyRenderType;
  renderString: string;
  scdEvent?: string;
}

export interface RenderEmailProperty extends BaseRenderProperty {
  raw: string;
  type: PropertyRenderType.Email;
  title: string;
}

export interface RenderLinkProperty extends BaseRenderProperty {
  raw: string;
  type: PropertyRenderType.Link;
  href: string;
  title: string;
}

export interface RenderStringProperty extends BaseRenderProperty {
  raw: string | number;
  type: PropertyRenderType.String;
  isEmpty: boolean;
  title?: Translatable;
}

export interface RenderTransactionProperty extends BaseRenderProperty {
  raw: Array<{$amount: number}> | string;
  type: PropertyRenderType.String;
}

export interface RenderNumberProperty extends BaseRenderProperty {
  raw: number;
  type: PropertyRenderType.String;
}

export type RenderValue =
  | RenderEmailProperty
  | RenderLinkProperty
  | RenderStringProperty
  | RenderNumberProperty
  | RenderTransactionProperty;

export interface FormattedProperty {
  name: {
    formatted: string;
    raw: string;
  };
  renderValue: RenderValue;
}

export const getRawPropertyValue = (prop: FormattedProperty): ValidPropertyValue => prop?.renderValue?.raw;
export const getRenderInputPropertyValue = (prop: FormattedProperty): string => prop?.renderValue?.renderString;
export const getScdEventValue = (prop: FormattedProperty): string => prop?.renderValue?.scdEvent;
export const getFormattedPropertyName = (prop: FormattedProperty): string => prop?.name?.formatted;
export const getRawPropertyName = (prop: FormattedProperty): string => prop?.name?.raw;

export const OBFUSCATED_STRING = `******`;
export const OBFUSCATED_TOOLTIP = `Disabled due to Classified data`;

export function formatCountry(countryCode) {
  return COUNTRY_CODES[countryCode.toUpperCase?.()] ?? countryCode;
}

export function formatAgo(value: any, timezone: string): string {
  const {tz, utc} = momentTimezone;
  let ret = value;

  // We receive a timestamp in project time
  // We need to save this original time for displaying in the UI, but X hours ago calculations need to be done relative to local time
  // so we have to convert the received timestamp into the equivalent local time
  const momentInProjectTime = tz(value, timezone);
  if (momentInProjectTime.isValid()) {
    const momentInUTC = momentInProjectTime.utc();

    const nowUTC = utc();
    const weeksAgo = nowUTC.clone().subtract(2, `weeks`);

    if (momentInUTC.isAfter(weeksAgo)) {
      ret = momentInUTC.from(nowUTC);
    } else {
      ret = momentInProjectTime.format(`MMM D, YYYY`);
    }
  }

  return ret;
}

export function formatDateMoment(value: Moment, format: string = FULL_DISPLAY_DATE_FORMAT, utc = false): string {
  return formatDate(value.toDate(), {
    unit: `full`,
    utc,
    customFormatting: {
      full: format,
    },
  });
}

/**
 * @deprecated use {@link epochTimestampToProjectTimeZone} instead. UTC offset is not a stable value over time for a project.
 *
 * For example, US/West timezone is -8 hours during standard time, -7 during daylight savings. Displaying an event outside DST that happened during DST will always be off by one hour.
 *
 * Timestamps (i.e. seconds or MS from epoch time) must be treated *differently* from ISO8601 strings
 * This is because our ISO8601 strings are in the format `YYYY-MM-DDTHH:MM:SSZ`, and the `Z` at the end
 * makes moment treat them as UTC and not include local timezone manipulation. However, time *stamps*
 * do not have this luxury, and we must use the utcoffset to tell moment how much to offset by for project time.
 *
 * @param value - a timestamp in milliseconds from epoch time (if you have seconds, multiply by 1000)
 * @param utcOffset - minutes from utc
 */
export function epochTimestampToProjectTime(value: number, utcOffset: number): Moment {
  // current algorithm doesn't take DST at the time of the event into account.

  const time = moment.utc(value);
  time.add(utcOffset, `minutes`);
  return time;
}

/**
 * Timestamps (i.e. seconds or MS from epoch time) must be treated *differently* from ISO8601 strings
 * This is because our ISO8601 strings are in the format `YYYY-MM-DDTHH:MM:SSZ`, and the `Z` at the end
 * makes moment treat them as UTC and not include local timezone manipulation. However, time *stamps*
 * do not have this luxury, and we must use the utcoffset to tell moment how much to offset by for project time.
 *
 * @param value - a timestamp in milliseconds from epoch time (if you have seconds, multiply by 1000)
 * @param timezone - the name of the project timezone, so proper date math can be applied. e.g. `America/Los_Angeles`
 */
export function epochTimestampToProjectTimeZone(value: number, timezone: string) {
  const {tz, utc} = momentTimezone;
  return tz(utc(value), timezone);
}

/**
 * Takes an epoch timestamp value and creates a time in UTC
 *
 * @param value - a timestamp in milliseconds from epoch time (if you have seconds, multiply by 1000)
 */
export function epochTimestampToUtc(value: number) {
  return moment.utc(value);
}

/**
 * Transforms a single transaction, a list of transactions, or a json string representing one of the previous into a single number
 */
function formatTransactions(transactions: Array<{$amount: number}> | {$amount: number} | string): number {
  // Event properties can have JSON transaction properties
  if (isString(transactions)) {
    transactions = JSON.parse(transactions);
  }

  // Sometimes transactions shows up as a single transaction (or parsed from above)
  if (isPlainObject(transactions)) {
    transactions = [transactions as {$amount: number}];
  }
  // Sometimes transactions are actually transaction_sum, calculation made from backend
  if (isNumber(transactions)) {
    return transactions;
  }

  if (!isArray(transactions)) {
    reporter.warn(`Transactions was not an array of $amount values; could not format; returning 0`, transactions);
    return 0;
  }

  return transactions.reduce((acc, transaction) => acc + (isNumber(transaction?.$amount) ? transaction.$amount : 0), 0);
}

export const valueExists = function (value) {
  // NOT null, undefined, or NaN
  // BUT empty string, false and 0 are totally legit
  return value || false === value || 0 === value || `` === value;
};

export function getProfileUrl(distinctId: string, dataGroupId?: string): string {
  const url = `${getReportUrl(`profile`, {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
  })}#distinct_id=${encodeURIComponent(distinctId)}`;
  if (dataGroupId) {
    return `${url}&data_group_id=${dataGroupId}`;
  } else {
    return url;
  }
}

const renderDistinctId = (value: string, {dataGroupId}: {dataGroupId?: string}): RenderLinkProperty => ({
  raw: value,
  type: PropertyRenderType.Link,
  renderString: value,
  href: value === OBFUSCATED_STRING ? null : getProfileUrl(value, dataGroupId),
  title: value === OBFUSCATED_STRING ? OBFUSCATED_TOOLTIP : value,
});

const defaultRenderValue = (value: string): RenderStringProperty => {
  const isNotSet = !valueExists(value);
  const isEmpty = !isNotSet && !trim(String(value));
  const renderString = isNotSet
    ? ConstantPropertyValuesInfo[ConstantPropertyValues.NotSet].label
    : isEmpty
      ? ConstantPropertyValuesInfo[ConstantPropertyValues.EmptyString].label
      : trim(String(value));
  const title = isNotSet
    ? ConstantPropertyValuesInfo[ConstantPropertyValues.NotSet].tooltip.text
    : isEmpty
      ? ConstantPropertyValuesInfo[ConstantPropertyValues.EmptyString].tooltip.text
      : ``;

  return {
    raw: value,
    type: PropertyRenderType.String,
    // empty string should be rendered properly
    isEmpty: !valueExists(value) || value === ``,
    renderString,
    title,
  };
};

const renderEpochTime =
  ({isSeconds, useOffset = true}: {isSeconds: boolean; useOffset: boolean}) =>
  (value: number, {timezone, relativeTime}): RenderStringProperty => {
    const formatString = MILLISECOND_DISPLAY_DATE_FORMAT;
    const rawValue = value;

    if (isSeconds) {
      value *= 1000;
    }

    let renderString;
    if (relativeTime) {
      renderString = moment(new Date(value).toString()).fromNow();
    } else {
      const converter = useOffset ? epochTimestampToProjectTimeZone : epochTimestampToUtc;
      renderString = converter(value, timezone).format(formatString);
    }

    return {
      raw: rawValue,
      isEmpty: false,
      type: PropertyRenderType.String,
      renderString,
    };
  };

const DECIMAL_PRECISION = 4;
const BIG_NUMBER_PRECISION = 2;
const formatTransactionValue = (value) => {
  let visibleAmount = !Number.isInteger(value) ? value.toFixed(DECIMAL_PRECISION) : value;
  visibleAmount = trimZeroesNumber(visibleAmount.toString());
  return visibleAmount < 10000
    ? commaizeNumber(visibleAmount)
    : abbreviateNumber(visibleAmount, {precision: BIG_NUMBER_PRECISION, spaced: undefined, trimZeroes: true});
};

const renderTransaction = (value: any[] | string): RenderTransactionProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: formatTransactionValue(formatTransactions(value)),
});

const renderEmail = (value: string): RenderEmailProperty => {
  const trimmedValue = trim(value);

  return {
    raw: value,
    type: PropertyRenderType.Email,
    renderString: trimmedValue,
    title: trimmedValue === OBFUSCATED_STRING ? OBFUSCATED_TOOLTIP : trimmedValue,
  };
};

const renderUrl = (value: string): RenderLinkProperty => {
  const shortened = truncateMiddle(value, 60);

  let href = value;
  if (!href.includes(`://`)) {
    href = `http://` + href;
  }

  return {
    raw: value,
    type: PropertyRenderType.Link,
    renderString: shortened,
    href,
    title: value === OBFUSCATED_STRING ? OBFUSCATED_TOOLTIP : value,
  };
};

const renderLastSeen = (value: string, {timezone}): RenderStringProperty => ({
  raw: value,
  isEmpty: false,
  type: PropertyRenderType.String,
  renderString: formatAgo(value, timezone),
  title: `Updated at ${formatDateMoment(moment(value, moment.ISO_8601, true))}`,
});

const renderCountry = (value: string): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  isEmpty: false,
  renderString: formatCountry(value),
});

const renderDate = (value: string): RenderStringProperty => {
  const maybeDate = moment(value, `YYYY-MM-DDTHH:mm:ss`, true);
  if (maybeDate.isValid()) {
    return {
      raw: value,
      type: PropertyRenderType.String,
      isEmpty: false,
      renderString: formatDateMoment(maybeDate),
    };
  }

  // False alarm, this wasn't a date, so return a normal string property
  return defaultRenderValue(value);
};

const renderObfuscated = (value: string): RenderStringProperty => ({
  raw: value,
  type: PropertyRenderType.String,
  renderString: value,
  isEmpty: false,
  title: OBFUSCATED_TOOLTIP,
});

const renderJSON = (value: any): RenderStringProperty => ({
  raw: value,
  isEmpty: false,
  type: PropertyRenderType.String,
  renderString: JSON.stringify(value),
});

type PropertyTester = (propertyName: string, propertyValue: ValidPropertyValue) => boolean;
type PropertyFormatter = (
  propertyValue: ValidPropertyValue,
  opts: {
    timezone?: string;
    relativeTime?: boolean;
    dataGroupId?: string;
  },
) => RenderValue;

const PROPERTY_FORMATTERS: Array<{
  test: string | PropertyTester;
  formatter: PropertyFormatter;
}> = [
  {test: MPProperties.Time, formatter: renderEpochTime({isSeconds: true, useOffset: true})},
  {test: MPProperties.$Time, formatter: renderEpochTime({isSeconds: true, useOffset: true})},
  {test: MPProperties.MPProcessingTime, formatter: renderEpochTime({isSeconds: false, useOffset: false})},
  {test: MPProperties.Transactions, formatter: renderTransaction},
  {test: MPProperties.DistinctId, formatter: renderDistinctId},
  // In live view distinct_id does not come back with the $ - TODO consolidate
  {test: (propertyName: string, _) => propertyName === `distinct_id`, formatter: renderDistinctId},
  {test: (propertyName: string, _) => /^\$?email$/i.test(propertyName), formatter: renderEmail},
  {test: (_, propertyValue) => isURL(propertyValue), formatter: renderUrl},
  {test: MPProperties.LastSeen, formatter: renderLastSeen},
  {test: MPProperties.CountryCode, formatter: renderCountry},
  {test: MPProperties.MPCountryCode, formatter: renderCountry},
  {test: (_: string, propertyValue: string) => propertyValue === OBFUSCATED_STRING, formatter: renderObfuscated},
  {
    test: (_: string, propertyValue: string) => isPlainObject(propertyValue) || isArray(propertyValue),
    formatter: renderJSON,
  },
  // Always have the date formatter next to last, since it's the most "expensive" one and has a bail out condition for 'almost' dates
  {
    test: (_: string, propertyValue: string) => {
      // Since `moment()` is very expensive (can be ~30% of our perf time in this function), attempt parse as a date
      // only if it matches an initial date check
      const maybeDate = new Date(propertyValue);

      // stolen from https://stackoverflow.com/a/10589791
      return maybeDate instanceof Date && !isNaN(maybeDate.valueOf());
    },
    formatter: renderDate,
  },
  {test: () => true, formatter: defaultRenderValue},
];

interface FormatPropertyOptions {
  // Needed for renaming property names
  dataGroupId?: string;

  // default to be User
  resourceType?: ResourceType;

  // format time property in relative notion
  relativeTime?: boolean;

  /**
   * Use this for more precise date math when formatting a date property that needs to be adjusted for project time zone
   *
   * 'lastSeen' is assumed to be stored in the project timezone, and the timezone helps with the "time ago" calc.
   * 'processed time' is stored UTC and always displayed UTC.
   * Other time properties are stored as UTC and timezone helps display the value in project time.
   */
  timezone?: string;

  i18n: LocalizationStore;
}

export function formatProperty(
  propertyName: string,
  propertyValue: any,
  {dataGroupId, resourceType, timezone, i18n, relativeTime = false}: FormatPropertyOptions,
): FormattedProperty {
  if (!resourceType) {
    resourceType = ResourceType.User;
  }
  const name = {
    formatted: propertiesUtils.renameProperty(propertyName, resourceType, i18n, {dataGroupId}),
    raw: propertyName,
  };

  if (!propertyValue) {
    return {
      name,
      renderValue: defaultRenderValue(propertyValue),
    };
  }

  const {formatter} = PROPERTY_FORMATTERS.find(({test}) =>
    isString(test) ? propertyName === test : test(propertyName, propertyValue),
  );

  return {
    name,
    renderValue: formatter(propertyValue, {
      timezone,
      ...(propertyName === MPProperties.Time || propertyName === MPProperties.$Time ? {relativeTime} : {}),
      dataGroupId,
    }),
  };
}

export function formatUserProperties(
  properties: {[p: string]: any},
  formatOptions: FormatPropertyOptions,
): Map<string, FormattedProperty> {
  return Object.entries(properties).reduce(
    (acc, [propertyName, propertyValue]: [string, any]): Map<string, FormattedProperty> => {
      acc.set(propertyName, formatProperty(propertyName, propertyValue, formatOptions));

      return acc;
    },
    new Map(),
  );
}

export function getNameProperties(properties: Map<string, FormattedProperty>): ProfileNameProperties {
  return NAME_PROPERTIES.reduce((profile, propertyName) => {
    profile[propertyName] = getRawPropertyValue(properties.get(propertyName)) as string;
    return profile;
  }, {});
}
